import type { ApiPlayable } from "@hive/sdk";
import { hive } from "./apiClient";
import { playableLevel } from "./catalogueLevel";
export { playableLevel } from "./catalogueLevel";

export function publicGame(item: ApiPlayable, now = Date.now()): boolean {
	return item.kind === "game" && item.status === "PUBLISHED" && item.visibility === "PUBLIC" &&
		!item.deletedAt && (!item.expiresAt || Date.parse(item.expiresAt) > now);
}

function tagsOf(item: ApiPlayable): string[] {
	return [...new Set((item.tags ?? []).map((tag) => tag.trim().toLowerCase()).filter(Boolean))];
}

/** Similarity gates the candidate set; SP comes first within that set. */
export function rankSimilarGames(current: ApiPlayable, candidates: ApiPlayable[], limit = 24): ApiPlayable[] {
	const wanted = new Set(tagsOf(current));
	const unique = new Map<string, ApiPlayable>();
	for (const item of candidates) {
		if (item.id !== current.id && publicGame(item)) unique.set(item.id, item);
	}
	return [...unique.values()].map((item) => ({
		item,
		shared: tagsOf(item).filter((tag) => wanted.has(tag)).length,
		sp: playableLevel(item) === "SP" ? 1 : 0,
	})).filter(({ shared }) => !wanted.size || shared > 0)
		.sort((a, b) => b.sp - a.sp || b.shared - a.shared || b.item.playCount - a.item.playCount || a.item.id.localeCompare(b.item.id))
		.slice(0, limit).map(({ item }) => item);
}

/** Bounded, guest-safe reads. Tag responses include spec.level; summaries do not. */
export async function loadSimilarGames(current: ApiPlayable): Promise<ApiPlayable[]> {
	const tags = tagsOf(current).slice(0, 4);
	const requests = tags.length
		? tags.map((tag) => hive.tags.playables(tag, { limit: 50 }))
		: [hive.feed.forYou({ kind: "game", sort: "hot", limit: 50 })];
	const results = await Promise.allSettled(requests);
	if (results.every((result) => result.status === "rejected")) throw new Error("Recommendations unavailable");
	return rankSimilarGames(current, results.flatMap((result) => result.status === "fulfilled" ? result.value.items : []));
}

export function randomPublicGame(items: ApiPlayable[], random = Math.random): ApiPlayable | null {
	const games = [...new Map(items.filter((item) => publicGame(item)).map((item) => [item.id, item])).values()];
	return games[Math.min(games.length - 1, Math.floor(random() * games.length))] ?? null;
}
